'use client';

import {
  freddyTheme,
  StickyBar as FreddyStickyBar,
  useMediaQuery
} from '@coolblue-development/freddy';
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  type MutableRefObject,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

type Props = {
  children: ReactNode;
};

type Context = {
  triggerRef: MutableRefObject<HTMLElement | null>;
};

const StickyBarContext = createContext<null | Context>(null);

export function StickyBarProvider({ children }: Props) {
  const triggerRef = useRef<HTMLElement>(null);

  return <StickyBarContext.Provider value={{ triggerRef }}>{children}</StickyBarContext.Provider>;
}

export function StickyBarTrigger({ children }: Props) {
  const context = useContext(StickyBarContext);
  // Ensure that the StickyBarTrigger receives only one child
  const child = Children.only(children);

  if (!context || !isValidElement<{ ref: Context['triggerRef'] }>(child)) {
    return null;
  }

  return cloneElement(child, { ref: context.triggerRef });
}

export function StickyBarContent({ children }: Props) {
  const isMediumBreakpointOrUp = useMediaQuery(freddyTheme.breakpoint.minWidth('md'), true);
  const context = useContext(StickyBarContext);

  const [isIntersecting, setIsIntersecting] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    });

    if (context?.triggerRef.current) {
      observer.observe(context.triggerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [context]);

  if (!isMediumBreakpointOrUp) {
    return <FreddyStickyBar placement='bottom'>{children}</FreddyStickyBar>;
  }

  return (
    !isIntersecting && (
      <FreddyStickyBar
        sx={{ height: 'auto !important', minHeight: 'var(--cb-sticky-bar-height)' }}
        offsetTop='var(--cb-header-height)'
        placement='top'
        // Prevent infinite loop on safari since with position fixed, there's no layout shift when scrolling
        // https://github.com/thebuilder/react-intersection-observer/issues/308#issuecomment-645537678
        position='fixed'
      >
        {children}
      </FreddyStickyBar>
    )
  );
}
